package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.models.StoricoEntry
import com.heelion.farmafreelance.utils.TimeUtils
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlin.math.round

/** I turni svolti presso una farmacia in un mese, con quanto è già stato pagato. */
data class PagamentiFarmacia(
    val farmaciaId: String,
    val nomeFarmacia: String,
    val turni: List<StoricoEntry>
) {
    val ore: Double get() = turni.sumOf { TimeUtils.calculateHours(it.oraInizio, it.oraFine) }
    val importo: Double get() = arrotonda(turni.sumOf { ResocontoPagamenti.importo(it) })
    val daIncassare: Double get() = arrotonda(turni.filter { it.pagatoIl == null }.sumOf { ResocontoPagamenti.importo(it) })
    val tuttoPagato: Boolean get() = turni.all { it.pagatoIl != null }
    val nessunoPagato: Boolean get() = turni.none { it.pagatoIl != null }
    /** Data dell'ultimo pagamento segnato (per "pagato il …"). */
    val pagatoIl: Instant? get() = turni.mapNotNull { it.pagatoIl }.maxOrNull()
}

/** Un mese dell'agenda dei pagamenti ([mese] 1..12). */
data class MesePagamenti(val anno: Int, val mese: Int, val farmacie: List<PagamentiFarmacia>) {
    val daIncassare: Double get() = arrotonda(farmacie.sumOf { it.daIncassare })
    val importo: Double get() = arrotonda(farmacie.sumOf { it.importo })
}

/**
 * Agenda personale dei pagamenti del farmacista: i turni svolti (storico) raggruppati per mese e
 * per farmacia, con importo (ore x tariffa oraria, come fatture e guadagni) e quanto resta da
 * incassare. "Pagato" lo segna il farmacista quando riceve il pagamento.
 */
object ResocontoPagamenti {

    fun importo(t: StoricoEntry): Double = TimeUtils.calculateHours(t.oraInizio, t.oraFine) * t.compenso

    /** Mesi dal più recente; in ogni mese le farmacie con qualcosa da incassare prima, poi per nome. */
    fun perMese(storico: List<StoricoEntry>): List<MesePagamenti> =
        storico.groupBy { giorno(it).let { g -> g.year to g.monthNumber } }
            .toList()
            .sortedWith(compareByDescending<Pair<Pair<Int, Int>, List<StoricoEntry>>> { it.first.first }.thenByDescending { it.first.second })
            .map { (annoMese, turni) ->
                val farmacie = turni.groupBy { it.farmaciaId }.map { (id, suoi) ->
                    PagamentiFarmacia(id, suoi.firstNotNullOfOrNull { it.nomeFarmacia } ?: "Farmacia", suoi.sortedBy { it.dataTurno })
                }.sortedWith(compareBy<PagamentiFarmacia> { it.tuttoPagato }.thenBy { it.nomeFarmacia.lowercase() })
                MesePagamenti(annoMese.first, annoMese.second, farmacie)
            }

    /** Chi deve ancora pagare, su tutti i mesi: farmacia -> importo, dal più alto. */
    fun daIncassarePerFarmacia(storico: List<StoricoEntry>): List<Pair<String, Double>> =
        storico.filter { it.pagatoIl == null }
            .groupBy { it.nomeFarmacia ?: "Farmacia" }
            .map { (nome, turni) -> nome to arrotonda(turni.sumOf { importo(it) }) }
            .sortedByDescending { it.second }

    private val DOMANDA = Regex(
        "\\b(chi|quali farmacie|quanto|cosa)\\b.*\\b(pagat\\w*|pagare|pagarmi|pagamenti?|incass\\w*)\\b" +
            "|\\bda incassare\\b|\\bnon mi (ha|hanno) (ancora )?pagat|\\bmi (deve|devono) (ancora )?(pagare|soldi)|\\bpagamenti (in sospeso|da ricevere|aperti)"
    )

    /** "chi mi deve ancora pagare?", "quanto devo incassare?": domanda sull'agenda dei pagamenti (non l'abbonamento). */
    fun eDomandaPagamenti(testo: String): Boolean {
        val t = IntentClassifier.normalizza(testo)
        return DOMANDA.containsMatchIn(t) && !Regex("abbonament|premium|guadagn").containsMatchIn(t)
    }

    /** Risposta di Farmy: chi deve ancora pagare e quanto, o "tutto incassato". */
    fun risposta(storico: List<StoricoEntry>): String {
        if (storico.isEmpty()) return "Non hai ancora turni svolti, quindi niente da incassare."
        val aperti = daIncassarePerFarmacia(storico)
        if (aperti.isEmpty()) return "Hai incassato tutto: nessuna farmacia ti deve ancora pagare."
        val totale = arrotonda(aperti.sumOf { it.second })
        val elenco = aperti.take(5).joinToString("; ") { (nome, importo) -> "$nome ${euroBreve(importo)}" }
        return "Devi ancora incassare ${euroBreve(totale)} in tutto: $elenco" +
            (if (aperti.size > 5) " e altre ${aperti.size - 5}" else "") +
            ". Quando ti pagano biffalo in Profilo > Resoconto pagamenti."
    }

    /**
     * Nei primi 7 giorni del mese: promemoria dei turni del mese scorso non ancora segnati come
     * pagati ("Del mese di agosto devi ancora incassare 560 € da Farmacia Rossi."); null se niente.
     */
    fun promemoriaMeseScorso(storico: List<StoricoEntry>, oggi: kotlinx.datetime.LocalDate): String? {
        if (oggi.dayOfMonth > 7) return null
        val scorso = oggi.minus(1, kotlinx.datetime.DateTimeUnit.MONTH)
        val mese = perMese(storico).firstOrNull { it.anno == scorso.year && it.mese == scorso.monthNumber } ?: return null
        val aperte = mese.farmacie.filter { it.daIncassare > 0 }
        if (aperte.isEmpty()) return null
        return "Di ${NOMI_MESI[mese.mese - 1]} devi ancora incassare ${euroBreve(mese.daIncassare)} da " +
            aperte.joinToString(", ") { it.nomeFarmacia } + ". Quando ti pagano, biffalo nel Resoconto pagamenti."
    }

    private val NOMI_MESI = listOf("gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio",
        "agosto", "settembre", "ottobre", "novembre", "dicembre")

    /** "560 €", "1.470,50 €" */
    private fun euroBreve(v: Double): String {
        val centesimi = round(v * 100).toLong()
        val interi = (centesimi / 100).toString().reversed().chunked(3).joinToString(".").reversed()
        val cent = centesimi % 100
        return if (cent == 0L) "$interi €" else "$interi,${cent.toString().padStart(2, '0')} €"
    }

    // Come nel resto dell'app: il giorno del turno è la data UTC di dataTurno.
    fun giorno(t: StoricoEntry) = t.dataTurno.toLocalDateTime(TimeZone.UTC).date
}

private fun arrotonda(v: Double) = round(v * 100) / 100
